import { useState, useRef, KeyboardEvent } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Tag, Users, Trash2, Edit, Loader2, Search, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { PhoneInput, FlagImage, defaultCountries, guessCountryByPartialPhoneNumber, parseCountry } from "react-international-phone";
import "react-international-phone/style.css";

// ── Types ───────────────────────────────────────────────────────
interface ContactTag {
  id: string;
  user_id: string;
  name: string;
  color: string | null;
  created_at: string;
}

interface CustomContact {
  id: string;
  user_id: string;
  name: string;
  phone_number: string;
  is_active: boolean;
  created_at: string;
}

interface ContactTagRelation {
  contact_id: string;
  tag_id: string;
}

const TAG_COLORS = [
  "#6366f1", "#f43f5e", "#10b981", "#f59e0b", "#8b5cf6",
  "#06b6d4", "#ec4899", "#84cc16",
];

// ── Multi-Tag Input Component ───────────────────────────────────
function TagInput({
  tags,
  selectedTagIds,
  onChange,
  onCreateTag,
}: {
  tags: ContactTag[];
  selectedTagIds: string[];
  onChange: (ids: string[]) => void;
  onCreateTag: (name: string) => Promise<string | null>;
}) {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedTags = tags.filter((t) => selectedTagIds.includes(t.id));
  const trimmed = inputValue.trim().toLowerCase();

  const suggestions = trimmed
    ? tags.filter(
        (t) =>
          t.name.toLowerCase().includes(trimmed) &&
          !selectedTagIds.includes(t.id)
      )
    : [];

  const exactMatch = tags.find((t) => t.name.toLowerCase() === trimmed);
  const canCreate = trimmed.length > 0 && !exactMatch;

  const addTag = (id: string) => {
    if (!selectedTagIds.includes(id)) {
      onChange([...selectedTagIds, id]);
    }
    setInputValue("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeTag = (id: string) => {
    onChange(selectedTagIds.filter((tid) => tid !== id));
  };

  const handleCreateAndAdd = async () => {
    if (!trimmed || creating) return;
    setCreating(true);
    const newId = await onCreateTag(trimmed);
    setCreating(false);
    if (newId) {
      addTag(newId);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      if (suggestions.length > 0) {
        addTag(suggestions[0].id);
      } else if (canCreate) {
        handleCreateAndAdd();
      }
    }
    if (e.key === "Backspace" && inputValue === "" && selectedTagIds.length > 0) {
      removeTag(selectedTagIds[selectedTagIds.length - 1]);
    }
  };

  return (
    <div className="space-y-1.5">
      <div
        className="flex flex-wrap gap-1.5 p-2 min-h-[42px] rounded-md border border-input bg-secondary/40 cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {selectedTags.map((tag) => (
          <Badge
            key={tag.id}
            variant="outline"
            className="gap-1 text-xs pr-1"
            style={{ borderColor: tag.color || "#6366f1" }}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color || "#6366f1" }} />
            {tag.name}
            <button
              type="button"
              className="ml-0.5 hover:bg-secondary/60 rounded-full p-0.5"
              onClick={(e) => { e.stopPropagation(); removeTag(tag.id); }}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? "Digite tags separadas por vírgula..." : ""}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
        />
      </div>

      {showSuggestions && (suggestions.length > 0 || canCreate) && (
        <div className="border border-input rounded-md bg-popover shadow-md overflow-hidden">
          {suggestions.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent/10 flex items-center gap-2"
              onMouseDown={(e) => { e.preventDefault(); addTag(tag.id); }}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tag.color || "#6366f1" }} />
              {tag.name}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent/10 flex items-center gap-2 text-primary border-t border-input"
              onMouseDown={(e) => { e.preventDefault(); handleCreateAndAdd(); }}
            >
              <Plus className="h-3.5 w-3.5" />
              Criar tag "{trimmed}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────
export default function DashboardContacts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // ── State ──
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<ContactTag | null>(null);
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState(TAG_COLORS[0]);

  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<CustomContact | null>(null);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactTagIds, setContactTagIds] = useState<string[]>([]);
  const [contactActive, setContactActive] = useState(true);

  const [searchContacts, setSearchContacts] = useState("");
  const [filterTag, setFilterTag] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // ── Queries ──
  const { data: tags = [], isLoading: loadingTags } = useQuery({
    queryKey: ["contact_tags"],
    queryFn: async () => {
      const { data, error } = await supabase.from("contact_tags").select("*").order("name");
      if (error) throw error;
      return data as ContactTag[];
    },
    enabled: !!user,
  });

  const { data: contacts = [], isLoading: loadingContacts } = useQuery({
    queryKey: ["custom_contacts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("custom_contacts").select("*").order("name");
      if (error) throw error;
      return data as CustomContact[];
    },
    enabled: !!user,
  });

  const { data: contactTagRelations = [] } = useQuery({
    queryKey: ["custom_contact_tags"],
    queryFn: async () => {
      const { data, error } = await supabase.from("custom_contact_tags").select("contact_id, tag_id");
      if (error) throw error;
      return data as ContactTagRelation[];
    },
    enabled: !!user,
  });

  // ── Tag Mutations ──
  const saveTag = useMutation({
    mutationFn: async () => {
      if (editingTag) {
        const { error } = await supabase.from("contact_tags").update({ name: tagName, color: tagColor }).eq("id", editingTag.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("contact_tags").insert({ user_id: user!.id, name: tagName, color: tagColor });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact_tags"] });
      setTagDialogOpen(false);
      resetTagForm();
      toast({ title: editingTag ? "Tag atualizada" : "Tag criada" });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteTag = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("contact_tags").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact_tags"] });
      queryClient.invalidateQueries({ queryKey: ["custom_contact_tags"] });
      toast({ title: "Tag removida" });
    },
  });

  // ── Contact Mutations ──
  const saveContact = useMutation({
    mutationFn: async () => {
      const payload = {
        name: contactName,
        phone_number: contactPhone.replace(/\D/g, ""),
        is_active: contactActive,
      };

      let contactId: string;

      if (editingContact) {
        const { error } = await supabase.from("custom_contacts").update(payload).eq("id", editingContact.id);
        if (error) throw error;
        contactId = editingContact.id;
      } else {
        const { data, error } = await supabase.from("custom_contacts").insert({ ...payload, user_id: user!.id }).select("id").single();
        if (error) throw error;
        contactId = data.id;
      }

      // Sync tags: delete old, insert new
      await supabase.from("custom_contact_tags").delete().eq("contact_id", contactId);
      if (contactTagIds.length > 0) {
        const inserts = contactTagIds.map((tag_id) => ({ contact_id: contactId, tag_id }));
        const { error: tagError } = await supabase.from("custom_contact_tags").insert(inserts);
        if (tagError) throw tagError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom_contacts"] });
      queryClient.invalidateQueries({ queryKey: ["custom_contact_tags"] });
      setContactDialogOpen(false);
      resetContactForm();
      toast({ title: editingContact ? "Contato atualizado" : "Contato adicionado" });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteContact = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("custom_contacts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom_contacts"] });
      queryClient.invalidateQueries({ queryKey: ["custom_contact_tags"] });
      toast({ title: "Contato removido" });
    },
  });

  const bulkDeleteContacts = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from("custom_contacts").delete().in("id", ids);
      if (error) throw error;
    },
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ["custom_contacts"] });
      queryClient.invalidateQueries({ queryKey: ["custom_contact_tags"] });
      toast({ title: `${ids.length} contato(s) removido(s)` });
      setSelectedIds([]);
      setBulkDeleteOpen(false);
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  // ── Create tag inline (from TagInput) ──
  const createTagInline = async (name: string): Promise<string | null> => {
    const color = TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
    const { data, error } = await supabase
      .from("contact_tags")
      .insert({ user_id: user!.id, name, color })
      .select("id")
      .single();
    if (error) {
      toast({ title: "Erro ao criar tag", description: error.message, variant: "destructive" });
      return null;
    }
    queryClient.invalidateQueries({ queryKey: ["contact_tags"] });
    return data.id;
  };

  // ── Helpers ──
  const resetTagForm = () => { setEditingTag(null); setTagName(""); setTagColor(TAG_COLORS[0]); };
  const resetContactForm = () => { setEditingContact(null); setContactName(""); setContactPhone(""); setContactTagIds([]); setContactActive(true); };

  const openEditTag = (tag: ContactTag) => {
    setEditingTag(tag);
    setTagName(tag.name);
    setTagColor(tag.color || TAG_COLORS[0]);
    setTagDialogOpen(true);
  };

  const openEditContact = (c: CustomContact) => {
    setEditingContact(c);
    setContactName(c.name);
    setContactPhone(c.phone_number);
    setContactTagIds(contactTagRelations.filter((r) => r.contact_id === c.id).map((r) => r.tag_id));
    setContactActive(c.is_active);
    setContactDialogOpen(true);
  };

  const getContactTags = (contactId: string) => {
    const tagIds = contactTagRelations.filter((r) => r.contact_id === contactId).map((r) => r.tag_id);
    return tags.filter((t) => tagIds.includes(t.id));
  };

  const filteredContacts = contacts.filter((c) => {
    const matchesSearch = c.name.toLowerCase().includes(searchContacts.toLowerCase()) || c.phone_number.includes(searchContacts);
    if (filterTag === "all") return matchesSearch;
    if (filterTag === "none") return matchesSearch && !contactTagRelations.some((r) => r.contact_id === c.id);
    return matchesSearch && contactTagRelations.some((r) => r.contact_id === c.id && r.tag_id === filterTag);
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Contatos</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Cadastre contatos e tags para participar do aquecimento dos seus números.
        </p>
      </div>

      <Tabs defaultValue="contacts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="contacts" className="gap-1.5"><Users className="h-3.5 w-3.5" /> Contatos</TabsTrigger>
          <TabsTrigger value="tags" className="gap-1.5"><Tag className="h-3.5 w-3.5" /> Tags</TabsTrigger>
        </TabsList>

        {/* ── TAB: CONTATOS ── */}
        <TabsContent value="contacts" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar contato..." value={searchContacts} onChange={(e) => setSearchContacts(e.target.value)} className="pl-9 bg-secondary/40 border-warmz-glass-border" />
            </div>
            <Select value={filterTag} onValueChange={setFilterTag}>
              <SelectTrigger className="w-[180px] bg-secondary/40 border-warmz-glass-border">
                <SelectValue placeholder="Filtrar por tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as tags</SelectItem>
                <SelectItem value="none">Sem tag</SelectItem>
                {tags.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color || "#6366f1" }} />
                      {t.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedIds.length > 0 && (
              <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="gap-2">
                    <Trash2 className="h-4 w-4" /> Excluir ({selectedIds.length})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir contatos selecionados?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {selectedIds.length} contato(s) serão removidos permanentemente. Esta ação não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={(e) => { e.preventDefault(); bulkDeleteContacts.mutate(selectedIds); }}
                      disabled={bulkDeleteContacts.isPending}
                    >
                      {bulkDeleteContacts.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                      Excluir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button className="gap-2 glow-orange-sm" onClick={() => { resetContactForm(); setContactDialogOpen(true); }}>
              <Plus className="h-4 w-4" /> Novo Contato
            </Button>
          </div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={filteredContacts.length > 0 && filteredContacts.every((c) => selectedIds.includes(c.id))}
                            onCheckedChange={(checked) => {
                              if (checked) setSelectedIds(filteredContacts.map((c) => c.id));
                              else setSelectedIds([]);
                            }}
                            aria-label="Selecionar todos"
                          />
                        </TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Tags</TableHead>
                        <TableHead>Ativo</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loadingContacts ? (
                        Array.from({ length: 3 }).map((_, i) => (
                          <TableRow key={i}>
                            {Array.from({ length: 6 }).map((_, j) => (
                              <TableCell key={j}><Skeleton className="h-5 w-20" /></TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : filteredContacts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                            {contacts.length === 0 ? "Nenhum contato cadastrado ainda." : "Nenhum resultado encontrado."}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredContacts.map((c) => {
                          const cTags = getContactTags(c.id);
                          return (
                            <TableRow key={c.id} data-state={selectedIds.includes(c.id) ? "selected" : undefined} className="border-b border-warmz-glass-border/20 hover:bg-warmz-card-hover/40">
                              <TableCell>
                                <Checkbox
                                  checked={selectedIds.includes(c.id)}
                                  onCheckedChange={(checked) => {
                                    setSelectedIds((prev) => checked ? [...prev, c.id] : prev.filter((id) => id !== c.id));
                                  }}
                                  aria-label={`Selecionar ${c.name}`}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{c.name}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <FlagImage iso2={guessCountryByPartialPhoneNumber({ phone: "+" + c.phone_number.replace(/\D/g, ""), countries: defaultCountries }).country?.iso2 || "br"} size="20px" />
                                  <span className="font-mono text-sm">{c.phone_number}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                {cTags.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {cTags.map((tag) => (
                                      <Badge key={tag.id} variant="outline" className="gap-1 text-[10px]" style={{ borderColor: tag.color || undefined }}>
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tag.color || "#6366f1" }} />
                                        {tag.name}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-xs italic">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant={c.is_active ? "default" : "secondary"} className="text-[10px]">
                                  {c.is_active ? "Sim" : "Não"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditContact(c)}>
                                    <Edit className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive hover:bg-destructive/10" onClick={() => deleteContact.mutate(c.id)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* ── TAB: TAGS ── */}
        <TabsContent value="tags" className="space-y-4">
          <div className="flex justify-end">
            <Button className="gap-2 glow-orange-sm" onClick={() => { resetTagForm(); setTagDialogOpen(true); }}>
              <Plus className="h-4 w-4" /> Nova Tag
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {loadingTags ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="glass-card border-warmz-glass-border/50">
                  <CardContent className="p-4"><Skeleton className="h-8 w-full" /></CardContent>
                </Card>
              ))
            ) : tags.length === 0 ? (
              <p className="text-muted-foreground text-sm col-span-full text-center py-8">Nenhuma tag criada ainda.</p>
            ) : (
              tags.map((tag) => {
                const count = contactTagRelations.filter((r) => r.tag_id === tag.id).length;
                return (
                  <motion.div key={tag.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                    <Card className="glass-card border-warmz-glass-border/50 hover:border-primary/30 transition-colors">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: tag.color || "#6366f1" }} />
                          <div>
                            <p className="text-sm font-semibold text-foreground">{tag.name}</p>
                            <p className="text-xs text-muted-foreground">{count} contato{count !== 1 ? "s" : ""}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditTag(tag)}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive hover:bg-destructive/10" onClick={() => deleteTag.mutate(tag.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Dialog: Tag ── */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent className="glass-card border-warmz-glass-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{editingTag ? "Editar Tag" : "Nova Tag"}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Tags agrupam contatos para facilitar a seleção nos números.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={tagName} onChange={(e) => setTagName(e.target.value)} placeholder="Ex: Família, Trabalho..." className="bg-secondary/40 border-warmz-glass-border" />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex gap-2 flex-wrap">
                {TAG_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`w-7 h-7 rounded-full border-2 transition-all ${tagColor === c ? "border-foreground scale-110" : "border-transparent opacity-60 hover:opacity-100"}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setTagColor(c)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveTag.mutate()} disabled={!tagName.trim() || saveTag.isPending} className="glow-orange-sm">
              {saveTag.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingTag ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Contato ── */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="glass-card border-warmz-glass-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{editingContact ? "Editar Contato" : "Novo Contato"}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Contatos cadastrados podem receber mensagens de aquecimento dos seus números.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Nome do contato" className="bg-secondary/40 border-warmz-glass-border" />
            </div>
            <div className="space-y-2">
              <Label>Telefone</Label>
              <PhoneInput
                defaultCountry="br"
                value={contactPhone.startsWith("+") ? contactPhone : "+" + contactPhone}
                onChange={(phone) => setContactPhone(phone.replace(/\D/g, "").replace(/^0+/, ""))}
                inputClassName="!bg-secondary/40 !border-warmz-glass-border !text-foreground !w-full"
                countrySelectorStyleProps={{
                  buttonClassName: "!bg-secondary/40 !border-warmz-glass-border",
                }}
                className="!w-full"
              />
            </div>
            <div className="space-y-2">
              <Label>Tags</Label>
              <TagInput
                tags={tags}
                selectedTagIds={contactTagIds}
                onChange={setContactTagIds}
                onCreateTag={createTagInline}
              />
              <p className="text-xs text-muted-foreground">Digite e separe por vírgula. Tags novas são criadas automaticamente.</p>
            </div>
            <div className="flex items-center justify-between">
              <Label>Ativo</Label>
              <Switch checked={contactActive} onCheckedChange={setContactActive} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveContact.mutate()} disabled={!contactName.trim() || !contactPhone.trim() || saveContact.isPending} className="glow-orange-sm">
              {saveContact.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingContact ? "Salvar" : "Adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
